import * as React from "react";
import { usePersonation } from "@/lib/personation-context";
import { applyLinkRegexReplaces } from "@/lib/url-utils";
import { useSourceNavigation } from "@/lib/use-source-navigation";
import { useSourceLookupOptional } from "@/lib/source-lookup-context";

export type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children: React.ReactNode;
};

/**
 * Anchor component that is PDF-aware via SourceLookupContext.
 *
 * When rendered inside a SourceLookupProvider, it checks whether the
 * href corresponds to a known PDF source and opens the built-in viewer
 * instead of navigating away.
 */
export function Anchor({ children, onClick, ...props }: AnchorProps): React.JSX.Element {
  const { linkTarget, linkRegexReplaces } = usePersonation();
  const sourceLookup = useSourceLookupOptional();
  const { navigateToUrl } = useSourceNavigation();

  const transformedHref = React.useMemo(
    () => applyLinkRegexReplaces(props.href ?? "", linkRegexReplaces),
    [props.href, linkRegexReplaces]
  );

  const targetAttr = props.target ?? linkTarget;
  const relAttr =
    targetAttr === "_blank"
      ? props.rel ?? "noopener noreferrer"
      : props.rel;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClick?.(e);

    const source = sourceLookup?.findSourceByUrl({ url: props.href ?? "" });

    navigateToUrl({
      url: props.href ?? "",
      fetchUrl: source?.fetch_url,
      title: source?.title,
      description: source?.description,
      keywords: source?.keywords,
      og_image: source?.og_image,
    });
  };

  return (
    <a {...props} href={transformedHref} target={targetAttr} rel={relAttr} onClick={handleClick}>
      {children}
    </a>
  );
}
